import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../context/AuthContext";
import {
  Plane, Lock, Clock, ChevronRight, BookOpen,
  Trophy, Loader2, CalendarDays
} from "lucide-react";

const MODULE_COLORS = [
  { bg: "bg-sky-100",    text: "text-sky-700",    border: "border-sky-200" },
  { bg: "bg-teal-100",   text: "text-teal-700",   border: "border-teal-200" },
  { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-200" },
  { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200" },
  { bg: "bg-rose-100",   text: "text-rose-700",   border: "border-rose-200" },
];

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
}

// ── Locked card ───────────────────────────────────────────────────────────────

function LockedCard({ mod, idx }) {
  const c = MODULE_COLORS[idx % MODULE_COLORS.length];
  return (
    <div data-testid={`locked-card-${mod.slug}`} className="relative rounded-2xl overflow-hidden">
      {/* Card body — greyed out */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 opacity-40 select-none pointer-events-none flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 ${c.bg} ${c.border} border rounded-xl flex items-center justify-center flex-shrink-0`}>
            <span className={`text-sm font-black ${c.text}`}
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              {String(mod.order_index).padStart(2, "0")}
            </span>
          </div>
          <span className="flex items-center gap-1 text-xs text-slate-400 font-medium">
            <Clock className="w-3 h-3" />{mod.estimated_read_minutes} min
          </span>
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-slate-900 text-base mb-2"
            style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {mod.title}
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{mod.description}</p>
        </div>
      </div>
      {/* Lock overlay */}
      <div className="absolute inset-0 flex items-center justify-center rounded-2xl">
        <div className="bg-slate-100 border border-slate-200 rounded-full p-3 shadow-sm">
          <Lock className="w-5 h-5 text-slate-500" />
        </div>
      </div>
    </div>
  );
}

// ── Unlocked card ─────────────────────────────────────────────────────────────

function UnlockedCard({ mod, idx }) {
  const c = MODULE_COLORS[idx % MODULE_COLORS.length];
  return (
    <Link
      to={`/learn/${mod.slug}`}
      data-testid={`arrival-card-${mod.slug}`}
      className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-sky-300 hover:shadow-md transition-all group flex flex-col gap-4"
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 ${c.bg} ${c.border} border rounded-xl flex items-center justify-center flex-shrink-0`}>
          <span className={`text-sm font-black ${c.text}`}
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {String(mod.order_index).padStart(2, "0")}
          </span>
        </div>
        <span className="flex items-center gap-1 text-xs text-slate-400 font-medium">
          <Clock className="w-3 h-3" />{mod.estimated_read_minutes} min
        </span>
      </div>
      <div className="flex-1">
        <h2 className="font-bold text-slate-900 text-base mb-2 group-hover:text-sky-700 transition-colors"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {mod.title}
        </h2>
        <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{mod.description}</p>
      </div>
      <div className="flex items-center gap-1 text-xs font-bold text-sky-600 group-hover:gap-2 transition-all">
        <BookOpen className="w-3.5 h-3.5" />
        <span>Read guide</span>
        <ChevronRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ArrivalHubPage() {
  const [profile,  setProfile]  = useState(null);
  const [modules,  setModules]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [profileRes, modulesRes] = await Promise.all([
          apiRequest("get", "/profile"),
          apiRequest("get", "/learn/modules?type=arrival"),
        ]);
        if (active) {
          setProfile(profileRes.data);
          setModules(modulesRes.data || []);
        }
      } catch (_e) { /* handled by ProtectedAppRoute */ }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, []);

  const isCommitted = Boolean(profile?.committed_at);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>

      {/* Header */}
      <div className="mb-6">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600">Your Journey</span>
        <h1 className="text-3xl font-bold text-slate-900 mt-1"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
          Arrival Guide
        </h1>
        <p className="text-slate-500 mt-1.5 text-sm max-w-xl">
          Everything you need to prepare for life as a student-athlete in the US — from visas to your first practice.
        </p>
      </div>

      {/* Status banner */}
      {isCommitted ? (
        <div data-testid="committed-banner"
          className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Trophy className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-green-800 text-sm">
              Committed to {profile.committed_to_institution}
            </p>
            <p className="text-green-600 text-xs flex items-center gap-1 mt-0.5">
              <CalendarDays className="w-3 h-3" />
              Since {formatDate(profile.committed_at)}
            </p>
          </div>
          <span className="text-xs font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full border border-green-200">
            Unlocked
          </span>
        </div>
      ) : (
        <div data-testid="locked-banner"
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-amber-800 text-sm">Your Arrival Guide is locked</p>
            <p className="text-amber-600 text-xs mt-0.5">
              Commit to your college to unlock all {modules.length || 5} arrival guides.
            </p>
          </div>
          <Link
            to="/profile"
            data-testid="go-to-profile-cta"
            className="text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-full border border-amber-300 transition-colors whitespace-nowrap"
          >
            Go to Profile →
          </Link>
        </div>
      )}

      {/* Module grid */}
      {modules.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center max-w-lg mx-auto">
          <Plane className="w-14 h-14 text-slate-200 mx-auto mb-4" />
          <h3 className="font-bold text-slate-600 text-lg mb-2"
            style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
            Guides Coming Soon
          </h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            Five in-depth arrival guides are being prepared. Check back shortly.
          </p>
        </div>
      ) : (
        <div
          data-testid="arrival-module-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {modules.map((mod, idx) =>
            isCommitted
              ? <UnlockedCard key={mod.slug} mod={mod} idx={idx} />
              : <LockedCard   key={mod.slug} mod={mod} idx={idx} />
          )}
        </div>
      )}
    </div>
  );
}
